import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../../../util/reactIntl';

import { NamedLink } from '../../../../../components';

import css from './EditorialCard.module.css';

/**
 * Editorial article card.
 *
 * Used both inside the landing-page `SectionEditorial` strip and as the
 * grid cell on the `MagasinPage` index. Wraps the whole card in a
 * NamedLink to `MagasinArticlePage` so the card is one big tap target.
 *
 * Two visual variants:
 * - `featured` — wide card with a large image, used as the first card in
 *   the editorial row.
 * - `regular` (default) — smaller card sitting alongside others in a grid.
 *
 * @param {Object} props
 * Shows the publication date, which the client asked for on the front page
 * for SEO: it is also emitted as `datePublished` in the article page's
 * structured data, and a visible date is what makes that claim checkable.
 * Marked up as a <time> so the machine-readable value travels with it.
 *
 * @param {Object} props.editorial - entry from src/config/editorials.js
 *   (slug, eyebrow, title, lede, image, imageAlt, publishedAt)
 * @param {boolean} [props.featured=false]
 * @returns {JSX.Element}
 */
const EditorialCard = props => {
  const intl = useIntl();
  const { editorial, featured = false } = props;
  const { slug, eyebrow, title, lede, image, imageAlt = '', publishedAt } = editorial;

  const publishedLabel = publishedAt
    ? intl.formatDate(new Date(publishedAt), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  return (
    <NamedLink
      className={css.root}
      name="MagasinArticlePage"
      params={{ slug }}
    >
      <div
        className={classNames(css.imageWrapper, {
          [css.aspectFeatured]: featured,
          [css.aspectRegular]: !featured,
        })}
      >
        <img className={css.image} src={image} alt={imageAlt} loading="lazy" />
      </div>

      <div className={css.body}>
        {eyebrow ? <span className={css.tag}>{eyebrow}</span> : null}
        <h4 className={classNames(css.title, { [css.titleFeatured]: featured })}>
          {title}
        </h4>
        {lede ? (
          <p className={classNames(css.excerpt, { [css.excerptFeatured]: featured })}>
            {lede}
          </p>
        ) : null}
        {publishedLabel ? (
          <time className={css.published} dateTime={publishedAt}>
            {publishedLabel}
          </time>
        ) : null}
      </div>
    </NamedLink>
  );
};

export default EditorialCard;
